import React, { useMemo } from 'react';

/* ─── Constants ─────────────────────────────────────────────────────────────── */

const NAKSHATRA_RASHI = {
  'Ashwini': 'Mesha', 'Bharani': 'Mesha', 'Krittika': 'Vrishabha',
  'Rohini': 'Vrishabha', 'Mrigashira': 'Mithuna', 'Ardra': 'Mithuna',
  'Punarvasu': 'Karka', 'Pushya': 'Karka', 'Ashlesha': 'Karka',
  'Magha': 'Simha', 'Purva Phalguni': 'Simha', 'Uttara Phalguni': 'Kanya',
  'Hasta': 'Kanya', 'Chitra': 'Tula', 'Swati': 'Tula',
  'Vishakha': 'Vrishchika', 'Anuradha': 'Vrishchika', 'Jyeshtha': 'Vrishchika',
  'Mula': 'Dhanu', 'Purva Ashadha': 'Dhanu', 'Uttara Ashadha': 'Makara',
  'Shravana': 'Makara', 'Dhanishtha': 'Kumbha', 'Shatabhisha': 'Kumbha',
  'Purva Bhadrapada': 'Meena', 'Uttara Bhadrapada': 'Meena', 'Revati': 'Meena',
};

const HORA_COLORS = {
  Sun: '#FCD34D', Moon: '#C4B5FD', Mars: '#F87171',
  Mercury: '#6EE7B7', Jupiter: '#FDE68A', Venus: '#F9A8D4', Saturn: '#94A3B8',
};

/* Sanskrit vara names (English day → Sanskrit weekday) */
const VARA_SANSKRIT = {
  Sunday: 'Ravivara', Monday: 'Somavara', Tuesday: 'Mangalavara',
  Wednesday: 'Budhavara', Thursday: 'Guruvara', Friday: 'Shukravara',
  Saturday: 'Shanivara',
};

/* ─── Static star field (deterministic so no re-render flicker) ─────────────── */

const STARS = Array.from({ length: 62 }, (_, i) => ({
  cx: `${((i * 37 + 13) % 98).toFixed(1)}%`,
  cy: `${((i * 61 + 17) % 95).toFixed(1)}%`,
  r: i % 9 === 0 ? 1.9 : i % 4 === 0 ? 1.1 : 0.55,
  opacity: +(0.22 + (i % 7) * 0.09).toFixed(2),
  fill: i % 5 === 0 ? '#ffffff' : i % 3 === 0 ? '#FFD966' : '#DDD6FE',
  twinkle: i % 3 === 0,
  dur: `${1.2 + (i % 5) * 0.45}s`,
}));

/* ─── Handmade SVG Icons ─────────────────────────────────────────────────────── */

/* Crescent moon + 4-pointed star */
const IconMoonStar = ({ color = 'currentColor' }) => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden>
    <path
      d="M14 11c0 3.8-3 6.5-6.8 6.5-1.1 0-2.1-.3-3-.8
         1.4.4 3.1.1 4.3-.9 2-1.4 3.1-3.7 2.9-6.1
         -.2-2.4-1.7-4.4-3.9-5.2.8-.2 1.7-.1 2.5.3
         C12.6 6.5 14 8.6 14 11z"
      fill={color} opacity="0.9"
    />
    <path
      d="M5.8 4.8 L6.25 6.3 L7.7 6.8 L6.25 7.3 L5.8 8.8 L5.35 7.3 L3.9 6.8 L5.35 6.3 Z"
      fill={color} opacity="0.75"
    />
  </svg>
);

/* 5-petal lotus — represents panchang (5 limbs) */
const IconLotus = ({ color = 'currentColor' }) => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden>
    {[0, 72, 144, 216, 288].map(a => (
      <ellipse
        key={a} cx="10" cy="7" rx="1.9" ry="4.3"
        fill={color} opacity="0.58"
        transform={`rotate(${a}, 10, 11)`}
      />
    ))}
    <circle cx="10" cy="11" r="2.4" fill={color} opacity="0.88" />
  </svg>
);

/* Ornate compass clock — cardinal + diagonal ticks + two hands */
const IconCompassClock = ({ color = 'currentColor' }) => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden>
    <circle cx="10" cy="10" r="8.3" stroke={color} strokeWidth="1" opacity="0.7" />
    {[0, 90, 180, 270].map(a => {
      const r = (a - 90) * Math.PI / 180;
      return (
        <line key={a}
          x1={+(10 + 6.5 * Math.cos(r)).toFixed(1)} y1={+(10 + 6.5 * Math.sin(r)).toFixed(1)}
          x2={+(10 + 8.3 * Math.cos(r)).toFixed(1)} y2={+(10 + 8.3 * Math.sin(r)).toFixed(1)}
          stroke={color} strokeWidth="1.3" strokeLinecap="round"
        />
      );
    })}
    {[45, 135, 225, 315].map(a => {
      const r = (a - 90) * Math.PI / 180;
      return (
        <line key={a}
          x1={+(10 + 7.1 * Math.cos(r)).toFixed(1)} y1={+(10 + 7.1 * Math.sin(r)).toFixed(1)}
          x2={+(10 + 8.3 * Math.cos(r)).toFixed(1)} y2={+(10 + 8.3 * Math.sin(r)).toFixed(1)}
          stroke={color} strokeWidth="0.6" strokeLinecap="round" opacity="0.55"
        />
      );
    })}
    {/* Hour hand */}
    <line x1="10" y1="10" x2="13.2" y2="6.8" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
    {/* Minute hand */}
    <line x1="10" y1="10" x2="8.8" y2="13.8" stroke={color} strokeWidth="0.8" strokeLinecap="round" opacity="0.6" />
    <circle cx="10" cy="10" r="1" fill={color} />
  </svg>
);

/* Eclipse / Rahu — dashed ring + shadow crescent */
const IconEclipse = ({ color = '#E07070' }) => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden>
    <circle cx="10" cy="10" r="8" stroke={color} strokeWidth="1.2" strokeDasharray="2.6 1.6" opacity="0.65" />
    {/* shadow crescent */}
    <path
      d="M10 2 A8 8 0 0 0 10 18 A4.5 8 0 0 1 10 2"
      fill={color} opacity="0.28"
    />
    <circle cx="10" cy="10" r="1.4" fill={color} opacity="0.8" />
    <line x1="10" y1="2.2" x2="10" y2="17.8" stroke={color} strokeWidth="0.5" opacity="0.2" />
  </svg>
);

/* ─── Cosmic Clock Wheel v2 — hora-dot ring + progress arc ──────────────────── */
/*
 * Design:
 *   • 24 coloured dots arranged in a circle, one per hora, each dot coloured
 *     by its ruling planet.
 *   • Active dot: larger radius + pulsing outer-ring animation.
 *   • Thin progress arc drawn in the annular gap between dot-ring and inner
 *     disc, sweeping clockwise from 12 o'clock to the active hora position.
 *   • Inner glass disc: dark fill + hora-lord colour radial glow.
 *   • Planet symbol in hora-lord colour with soft drop-shadow glow.
 */

const CosmicClockWheel = ({ horaData, color, size = 76 }) => {
  const schedule     = horaData?.hora_schedule || [];
  const currentHora  = horaData?.current_hora;
  const symbol       = currentHora?.symbol || '☉';

  const cx      = size / 2;
  const cy      = size / 2;
  const dotsR   = size * 0.415;   /* radius of dot ring              */
  const innerR  = size * 0.278;   /* inner disc radius               */
  const arcR    = (dotsR + innerR) / 2 + 1; /* progress arc radius  */
  const gradId  = 'ccw-grad';
  const glowId  = 'ccw-glow';

  const currentIdx = schedule.findIndex(h => h.is_current);
  const n = schedule.length || 24;

  /* Dot positions & colours */
  const dots = useMemo(() => schedule.map((hora, i) => {
    const a = (i / n) * 2 * Math.PI - Math.PI / 2; // 12 o'clock = 0
    return {
      cx:        +(cx + dotsR * Math.cos(a)).toFixed(2),
      cy:        +(cy + dotsR * Math.sin(a)).toFixed(2),
      dotColor:  HORA_COLORS[hora.planet] || '#888',
      isCurrent: !!hora.is_current,
    };
  }), [schedule, cx, cy, dotsR, n]);

  /* Progress arc: sweeps from 12 o'clock to midpoint of active hora */
  const progressArc = useMemo(() => {
    if (currentIdx < 0 || n === 0) return null;
    const fraction  = (currentIdx + 0.5) / n;
    const endAngle  = fraction * 2 * Math.PI - Math.PI / 2;
    const largeArc  = fraction > 0.5 ? 1 : 0;
    const sx = +(cx + arcR * Math.cos(-Math.PI / 2)).toFixed(2);
    const sy = +(cy + arcR * Math.sin(-Math.PI / 2)).toFixed(2);
    const ex = +(cx + arcR * Math.cos(endAngle)).toFixed(2);
    const ey = +(cy + arcR * Math.sin(endAngle)).toFixed(2);
    /* If fraction ≈ 0 the arc degenerates — skip */
    if (Math.abs(sx - ex) < 0.5 && Math.abs(sy - ey) < 0.5) return null;
    return `M${sx},${sy} A${arcR},${arcR} 0 ${largeArc},1 ${ex},${ey}`;
  }, [currentIdx, n, cx, cy, arcR]);

  return (
    <svg
      width={size} height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ display: 'block', flexShrink: 0 }}
    >
      <defs>
        <radialGradient id={gradId} cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor="transparent" stopOpacity="0" />
        </radialGradient>
        <filter id={glowId} x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation="2" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* ── Backing disc ── */}
      <circle cx={cx} cy={cy} r={dotsR + 4} fill="rgba(30,27,75,0.70)" />

      {/* ── Subtle outer guide ring ── */}
      <circle cx={cx} cy={cy} r={dotsR} fill="none"
        stroke="rgba(168,85,247,0.18)" strokeWidth="0.6" />

      {/* ── 24 hora dots ── */}
      {dots.map((d, i) => (
        <g key={i}>
          {/* Pulsing outer ring on active dot */}
          {d.isCurrent && (
            <circle
              cx={d.cx} cy={d.cy} r={6}
              fill="none" stroke={d.dotColor} strokeWidth="1.2"
              style={{
                opacity: 0,
                animation: 'ccw-pulse 2.2s ease-out infinite',
                transformOrigin: `${d.cx}px ${d.cy}px`,
              }}
            />
          )}
          <circle
            cx={d.cx} cy={d.cy}
            r={d.isCurrent ? 3.4 : 1.7}
            fill={d.dotColor}
            opacity={d.isCurrent ? 1 : 0.32}
            filter={d.isCurrent ? `url(#${glowId})` : undefined}
          />
        </g>
      ))}

      {/* ── Progress arc (day elapsed) ── */}
      {progressArc && (
        <path
          d={progressArc}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          opacity="0.35"
        />
      )}

      {/* ── Inner glass disc ── */}
      <circle cx={cx} cy={cy} r={innerR} fill="rgba(15,15,35,0.96)" />
      <circle cx={cx} cy={cy} r={innerR} fill={`url(#${gradId})`} />
      <circle cx={cx} cy={cy} r={innerR} fill="none"
        stroke={color} strokeWidth="0.6" opacity="0.35" />

      {/* ── Planet symbol ── */}
      <text
        x={cx} y={cy + 7}
        textAnchor="middle"
        fontSize="17" fontFamily="serif"
        fill={color}
        style={{ filter: `drop-shadow(0 0 6px ${color})` }}
      >
        {symbol}
      </text>
    </svg>
  );
};

/* ─── Skeleton ───────────────────────────────────────────────────────────────── */

const Sk = ({ w = '72%', h = 11 }) => (
  <div className="cs-sk" style={{ width: w, height: h, borderRadius: 3, marginBottom: 5 }} />
);

/* ─── Section Header ─────────────────────────────────────────────────────────── */

const SectionHead = ({ icon, label, labelColor }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
    {icon}
    <span style={{
      fontFamily: 'Montserrat, sans-serif', fontSize: 8, fontWeight: 700,
      letterSpacing: '0.15em', textTransform: 'uppercase',
      color: labelColor || 'rgba(255,255,255,0.3)',
    }}>
      {label}
    </span>
  </div>
);

/* ─── Lightning Layer ────────────────────────────────────────────────────────── */
/*
 * Three jagged cosmic plasma bolts placed at left / right / center.
 * Each bolt has:
 *   • A wide blurred halo (glow behind the bolt)
 *   • A razor-thin bright core stroke
 * The animation is a double-strike pattern (flash → dim → flash) with
 * staggered durations so they never feel synchronised.
 */

// Bolt paths defined in a 100×45 coordinate space (matched to viewBox)
const BOLT_SEGS = [
  // left bolt
  'M14,0 L10,11 L16,11 L8,25 L15,25 L6,44',
  // right bolt
  'M83,0 L87,13 L81,13 L86,27 L80,27 L85,44',
  // center bolt
  'M52,0 L48,12 L55,12 L47,26 L54,26 L46,44',
];

// [duration, delay] for each bolt — prime-ish numbers avoid beat-frequency sync
const BOLT_TIMING = [
  ['8s',  '0s'],
  ['11s', '3.8s'],
  ['14s', '7.5s'],
];

// Radial flash origin per bolt (X position %)
const FLASH_ORIGINS = ['14%', '83%', '52%'];

const LightningLayer = () => (
  <>
    {/* SVG bolts — stretches to fill card */}
    <svg
      aria-hidden
      viewBox="0 0 100 45"
      preserveAspectRatio="none"
      style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        pointerEvents: 'none', zIndex: 0,
        overflow: 'visible',
      }}
    >
      <defs>
        {/* Wide blurred halo behind each bolt */}
        <filter id="lb-halo" x="-200%" y="-200%" width="500%" height="500%">
          <feGaussianBlur stdDeviation="2.8" />
        </filter>
        {/* Tight glow around the bolt core */}
        <filter id="lb-core" x="-120%" y="-120%" width="340%" height="340%">
          <feGaussianBlur stdDeviation="0.6" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {BOLT_SEGS.map((pts, i) => {
        const [dur, delay] = BOLT_TIMING[i];
        const anim = `cs-bolt-flash ${dur} ${delay} linear infinite`;
        return (
          <g key={i}>
            {/* Halo — wide, electric blue, blurred */}
            <path
              d={pts} fill="none"
              stroke="rgba(168,85,247,0.60)" strokeWidth="5"
              filter="url(#lb-halo)"
              style={{ opacity: 0, animation: anim }}
            />
            {/* Core — razor-thin, near-white */}
            <path
              d={pts} fill="none"
              stroke="rgba(235,225,255,0.96)" strokeWidth="0.5"
              filter="url(#lb-core)"
              style={{ opacity: 0, animation: anim }}
            />
          </g>
        );
      })}
    </svg>

    {/* Per-bolt radial card-flash overlays */}
    {FLASH_ORIGINS.map((origin, i) => {
      const [dur, delay] = BOLT_TIMING[i];
      return (
        <div
          key={i}
          aria-hidden
          style={{
            position: 'absolute', inset: 0,
            pointerEvents: 'none', zIndex: 0,
            background: `radial-gradient(ellipse at ${origin} 50%, rgba(168,85,247,0.13) 0%, transparent 60%)`,
            opacity: 0,
            animation: `cs-card-flash ${dur} ${delay} linear infinite`,
          }}
        />
      );
    })}
  </>
);

/* ─── Cell style ─────────────────────────────────────────────────────────────── */

const cell = { padding: '14px 16px', flex: '1 1 0', minWidth: 0 };

/* ─── Main Component ─────────────────────────────────────────────────────────── */

const CosmicSnapshot = ({ panchangData, muhuratData, horaData, loading, onOpenMuhurat, headerSlot }) => {
  const nakshatra = panchangData?.nakshatra?.name
    || (typeof panchangData?.nakshatra === 'string' ? panchangData.nakshatra : null);
  const rashi     = nakshatra ? NAKSHATRA_RASHI[nakshatra] : null;
  const tithi     = panchangData?.tithi?.full_name || panchangData?.tithi?.name;
  const yoga      = panchangData?.yoga?.name;
  const vara      = panchangData?.vara?.name;
  const karana    = panchangData?.karana?.name;
  const rahuKaal  = muhuratData?.rahu_kaal;

  const isLoading  = loading || (!panchangData && !horaData);
  const hasMuhurat = muhuratData && (muhuratData.schedule?.length > 0 || muhuratData.rahu_kaal);

  const hora      = horaData?.current_hora;
  const horaColor = HORA_COLORS[hora?.planet] || 'var(--gold-primary)';

  const panchangItems = [
    { label: 'Tithi',     value: tithi,     dot: '#FCD34D' },
    { label: 'Vara',      value: vara,      dot: '#A78BFA' },
    { label: 'Nakshatra', value: nakshatra, dot: '#6EE7B7' },
    { label: 'Yoga',      value: yoga,      dot: '#FDE68A' },
    { label: 'Karana',    value: karana,    dot: '#F9A8D4' },
  ];

  return (
    <div
      data-testid="cosmic-snapshot"
      className="jp-card-elevated"
      style={{
        position: 'relative', overflow: 'hidden',
        padding: 0,
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 38%, #1e1b4b 68%, #0f0f23 100%)',
        backdropFilter: 'blur(24px)',
        WebkitBackdropFilter: 'blur(24px)',
        border: '1px solid rgba(124,58,237,0.40)',
        borderLeft: `3px solid ${horaColor}`,
        boxShadow: `0 0 40px rgba(49,46,129,0.55), inset 0 0 60px rgba(15,15,35,0.50)`,
        transition: 'border-left-color 0.8s ease',
      }}
    >
      {/* ── Radial purple glow — mimics onboarding starfield highlight ── */}
      <div
        aria-hidden
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
          background: 'radial-gradient(ellipse at 80% 20%, rgba(124,58,237,0.50) 0%, rgba(49,46,129,0.22) 42%, transparent 68%)',
        }}
      />
      {/* ── Secondary glow — bottom-left warmth ── */}
      <div
        aria-hidden
        style={{
          position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0,
          background: 'radial-gradient(ellipse at 12% 88%, rgba(79,38,170,0.35) 0%, transparent 55%)',
        }}
      />
      {/* ── Glass sky star field ── */}
      <svg
        aria-hidden
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0 }}
      >
        {STARS.map((s, i) => (
          <circle
            key={i} cx={s.cx} cy={s.cy} r={s.r}
            fill={s.fill} opacity={s.opacity}
            style={s.twinkle
              ? { animation: `cs-tw ${s.dur} ease-in-out infinite alternate` }
              : undefined}
          />
        ))}
      </svg>

      {/* ── Cosmic lightning ── */}
      <LightningLayer />

      {/* ── Greeting header slot (from parent) ── */}
      {headerSlot && (
        <div style={{
          padding: '10px 18px 9px',
          borderBottom: '1px solid rgba(168,85,247,0.12)',
          position: 'relative', zIndex: 1,
          background: 'rgba(0,0,0,0.18)',
        }}>
          {headerSlot}
        </div>
      )}

      {/* ── Card header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '9px 18px 8px',
        borderBottom: '1px solid rgba(168,85,247,0.15)',
        position: 'relative', zIndex: 1,
      }}>
        <span style={{
          fontFamily: 'Montserrat, sans-serif', fontSize: 8, fontWeight: 700,
          letterSpacing: '0.17em', textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.28)',
        }}>
          Cosmic Snapshot · Today
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {vara && (
            <span style={{
              fontFamily: 'Montserrat, sans-serif', fontSize: 8,
              color: `${horaColor}55`,
            }}>
              {VARA_SANSKRIT[vara] || vara}
            </span>
          )}
          {hasMuhurat && onOpenMuhurat && (
            <button
              onClick={onOpenMuhurat}
              data-testid="muhurat-full-schedule-btn"
              style={{
                fontFamily: 'Montserrat, sans-serif', fontSize: 7.5, fontWeight: 700,
                color: 'rgba(201,168,76,0.65)',
                background: 'rgba(201,168,76,0.07)',
                border: '1px solid rgba(201,168,76,0.18)',
                borderRadius: 4, padding: '2px 7px',
                cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.1em',
              }}
            >
              Full Schedule
            </button>
          )}
        </div>
      </div>

      {/* ── 2 × 2 Grid ── */}
      <div style={{ display: 'flex', position: 'relative', zIndex: 1 }}>

        {/* Left column */}
        <div style={{
          flex: '1 1 50%', minWidth: 0,
          display: 'flex', flexDirection: 'column',
          borderRight: '1px solid rgba(168,85,247,0.15)',
        }}>

          {/* Moon Star */}
          <div style={{ ...cell, borderBottom: '1px solid rgba(168,85,247,0.15)' }}>
            <SectionHead
              icon={<IconMoonStar color="var(--gold-bright)" />}
              label="Moon Star"
              labelColor="rgba(201,168,76,0.48)"
            />
            {isLoading ? (
              <><Sk w="62%" h={15} /><Sk w="44%" h={9} /></>
            ) : (
              <>
                <div style={{
                  fontFamily: 'Cinzel, serif', fontSize: 14, fontWeight: 500,
                  color: 'var(--gold-bright)', marginBottom: 3,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {nakshatra || '—'}
                </div>
                {rashi && (
                  <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 9, color: 'var(--text-secondary)' }}>
                    Rashi · {rashi}
                  </div>
                )}
                <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 8, color: 'var(--text-tertiary)', marginTop: 2 }}>
                  Lunar position
                </div>
              </>
            )}
          </div>

          {/* Cosmic Clock */}
          <div style={cell}>
            <SectionHead
              icon={<IconCompassClock color={horaColor} />}
              label="Cosmic Clock"
              labelColor={`${horaColor}99`}
            />
            {isLoading ? (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div className="cs-sk" style={{ width: 72, height: 72, borderRadius: '50%', flexShrink: 0 }} />
                <div><Sk w={52} h={13} /><Sk w={44} h={9} /></div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <CosmicClockWheel horaData={horaData} color={horaColor} size={72} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: 'Cinzel, serif', fontSize: 13, fontWeight: 600,
                    color: horaColor, lineHeight: 1.25, marginBottom: 3,
                    textShadow: `0 0 10px ${horaColor}44`,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {hora?.planet ? `${hora.planet} Hora` : '—'}
                  </div>
                  {hora?.time_range && (
                    <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 8.5, color: 'var(--text-secondary)', marginBottom: 2 }}>
                      {hora.time_range}
                    </div>
                  )}
                  {hora?.quality && (
                    <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 8, color: `${horaColor}88`, textTransform: 'capitalize' }}>
                      {hora.quality} influence
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

        </div>

        {/* Right column */}
        <div style={{ flex: '1 1 50%', minWidth: 0, display: 'flex', flexDirection: 'column' }}>

          {/* Panchang — 5 elements */}
          <div style={{ ...cell, borderBottom: '1px solid rgba(168,85,247,0.15)' }}>
            <SectionHead
              icon={<IconLotus color="#A78BFA" />}
              label="Panchang"
              labelColor="rgba(167,139,250,0.52)"
            />
            {isLoading ? (
              <>{[1,2,3,4,5].map(i => <Sk key={i} w={`${42+i*10}%`} h={9} />)}</>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {panchangItems.map(({ label, value, dot }) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{
                      width: 5, height: 5, borderRadius: '50%',
                      background: dot, flexShrink: 0,
                      boxShadow: `0 0 5px ${dot}99`,
                    }} />
                    <span style={{
                      fontFamily: 'Montserrat, sans-serif', fontSize: 8,
                      color: 'rgba(255,255,255,0.3)', width: 50, flexShrink: 0,
                    }}>
                      {label}
                    </span>
                    <span style={{
                      fontFamily: 'Cinzel, serif', fontSize: 9, fontWeight: 500,
                      color: 'rgba(255,255,255,0.82)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {value || '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Rahu Kaal */}
          <div
            style={{ ...cell, cursor: hasMuhurat && onOpenMuhurat ? 'pointer' : 'default' }}
            onClick={hasMuhurat && onOpenMuhurat ? onOpenMuhurat : undefined}
          >
            <SectionHead
              icon={<IconEclipse color="#E07070" />}
              label="Rahu Kaal"
              labelColor="rgba(224,112,112,0.55)"
            />
            {isLoading ? (
              <><Sk w="68%" h={14} /><Sk w="46%" h={9} /></>
            ) : (
              <>
                <div style={{
                  fontFamily: 'Cinzel, serif', fontSize: 13, fontWeight: 500,
                  color: '#E07070', marginBottom: 3,
                  textShadow: '0 0 8px #E0707044',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {rahuKaal ? `${rahuKaal.start} – ${rahuKaal.end}` : '—'}
                </div>
                <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 8.5, color: 'var(--text-secondary)' }}>
                  Avoid this window
                </div>
                {hasMuhurat && onOpenMuhurat && (
                  <div style={{
                    marginTop: 5, fontFamily: 'Montserrat, sans-serif',
                    fontSize: 7.5, fontWeight: 700,
                    color: '#E07070aa', textTransform: 'uppercase', letterSpacing: '0.1em',
                  }}>
                    View schedule →
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      </div>

      {/* ── Styles ── */}
      <style>{`
        .cs-sk {
          background: linear-gradient(
            90deg,
            rgba(255,255,255,0.04) 25%,
            rgba(255,255,255,0.09) 50%,
            rgba(255,255,255,0.04) 75%
          );
          background-size: 200% 100%;
          animation: cs-shimmer 1.6s infinite;
        }
        @keyframes cs-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        @keyframes cs-tw {
          0%   { opacity: 0.06; }
          100% { opacity: 0.42; }
        }

        /* ── Cosmic Clock dot pulse ── */
        @keyframes ccw-pulse {
          0%   { transform: scale(1);   opacity: 0.75; }
          100% { transform: scale(2.8); opacity: 0; }
        }

        /* ── Cosmic lightning ── */

        /*
         * Double-strike pattern:
         *   1.5%  → still dark (tension before the strike)
         *   1.8%  → FLASH  (first strike — peak brightness)
         *   2.05% → dim    (brief afterglow / darkness between strikes)
         *   2.35% → FLASH  (second strike — slightly weaker)
         *   2.7%  → gone   (bolt fades instantly)
         *   rest  → dark until next cycle
         *
         * All three bolts share this keyframe; different animation-duration
         * values make each bolt feel independent and unpredictable.
         */
        @keyframes cs-bolt-flash {
          0%     { opacity: 0; }
          1.5%   { opacity: 0; }
          1.8%   { opacity: 1; }
          2.05%  { opacity: 0.08; }
          2.35%  { opacity: 0.82; }
          2.7%   { opacity: 0; }
          100%   { opacity: 0; }
        }

        /* Card-flash overlay: same timing, lower peak opacity */
        @keyframes cs-card-flash {
          0%     { opacity: 0; }
          1.5%   { opacity: 0; }
          1.8%   { opacity: 1; }
          2.05%  { opacity: 0.12; }
          2.35%  { opacity: 0.75; }
          2.7%   { opacity: 0; }
          100%   { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default CosmicSnapshot;
